import { IconBot } from '@developerz/ui';
import { useLocation } from '@solidjs/router';
import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { isAdminHost } from '../lib/host';
import { createHotkeys } from '../lib/hotkeys';
import { orgFromPath, orgHref, orgRouteSection } from '../lib/org';
import { createRailCollapse } from '../lib/rail-collapse';
import { isBleedSection } from '../lib/routes';
import { SESSION_TOKEN } from '../lib/session';
import { AccountStreamProvider } from './AccountStreamProvider';
import { AppSidebar } from './AppSidebar';
import { AppTopbar } from './AppTopbar';
import { CommandPalette } from './CommandPalette';
import { KeyOutageBanner } from './KeyOutageBanner';
import { LinkCandidatesBanner } from './LinkCandidatesBanner';
import { LoginErrorNotice } from './LoginErrorNotice';
import { ShortcutSheet } from './ShortcutSheet';

// App shell (spec §4): fixed sidebar (AppSidebar) beside the routed content
// column with its sticky topbar (AppTopbar) and the bot-disclosure footer.
// Mounted once as the router root. Below lg the sidebar is the left nav drawer
// (scrim + slide).
//
// It also owns the two global overlays — the ⌘K command palette and the `?`
// shortcut sheet. They sit OUTSIDE the `bare()` branch on purpose: the hotkeys
// are bound for the whole shell's lifetime, so a binding that opened nothing on
// the chrome-less onboarding funnel would be a dead key on exactly one screen.
//
// And the rail's collapse (#7710, `lib/rail-collapse.ts`): `[` and the rail's
// foot toggle flip it at `lg`, and `app-shell--rail-collapsed` narrows the rail
// to its icons. The one exception to the rule above: the funnel has no rail, so
// `[` there flips nothing a reader could see and is left alone.

export function AppShell(props: {
  /** Bearer for account-scoped reads. Defaults to `SESSION_TOKEN`; the test
   *  seam — without it `bun test` (where it is `null`) couldn't tell a mounted
   *  banner from an absent one. */
  readonly token?: string | null;
  readonly children?: JSX.Element;
}): JSX.Element {
  const location = useLocation();
  const [navOpen, setNavOpen] = createSignal(false);
  const [paletteOpen, setPaletteOpen] = createSignal(false);
  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);
  const rail = createRailCollapse();
  // Onboarding is the mock's only shell-less in-app page: full-screen setup
  // funnel with its own brand column — no sidebar/topbar/footer chrome.
  const bare = (): boolean => /\/onboarding\/?$/.test(location.pathname);
  // Full bleed (#4395, `RouteEntry.bleed`): the current route drops `.page`'s
  // title-and-cards wrapper. Off `/org/…` (admin routes, redirect limbo) there
  // is no org-scoped route to be bleed or not, so `orgRouteSection` returning
  // `null` reads as "not bleed" rather than throwing.
  const bleed = (): boolean => {
    const section = orgRouteSection(location.pathname);
    return section !== null && isBleedSection(section);
  };
  // #754: the admin host shows ONLY the radar chrome; the app host shows the
  // product chrome. Cosmetic — server scope + <AdminGate/> are the real gate.
  const adminHost = isAdminHost();
  // Org-prefixed links (#460): derived from the router location (reactive) so
  // the nav re-targets as the user moves between orgs; bare outside /org/:org.
  const nav = (path: string): string => orgHref(path, orgFromPath(location.pathname));

  // ⌘K toggles (pressing it again backs out the way it came in); `?` opens;
  // `[` collapses or expands the rail, consumed only when it acted (at `lg`).
  createHotkeys({
    onPalette: () => setPaletteOpen((open) => !open),
    onShortcuts: () => setShortcutsOpen(true),
    onRail: () => !bare() && rail.toggle(),
  });

  // Close the off-canvas drawer whenever the route changes (a nav pick navigates).
  createEffect(() => {
    location.pathname;
    setNavOpen(false);
  });

  // ONE `/v1/streams` connection for the whole tab (`lib/stream-topics.ts`).
  // It wraps the `bare()` branch too: the hub dials nothing until a live view
  // registers a topic, so the chrome-less funnel still costs no connection.
  return (
    <AccountStreamProvider>
      <CommandPalette
        open={paletteOpen()}
        adminHost={adminHost}
        resolve={nav}
        onClose={() => setPaletteOpen(false)}
      />
      <ShortcutSheet open={shortcutsOpen()} onClose={() => setShortcutsOpen(false)} />
      <Show when={!bare()} fallback={props.children}>
        <Shell
          adminHost={adminHost}
          nav={nav}
          navOpen={navOpen()}
          setNavOpen={setNavOpen}
          railCollapsed={rail.collapsed()}
          onToggleRail={() => rail.toggle()}
          bleed={bleed()}
          token={props.token === undefined ? SESSION_TOKEN : props.token}
          onOpenPalette={() => setPaletteOpen(true)}
          onOpenShortcuts={() => setShortcutsOpen(true)}
        >
          {props.children}
        </Shell>
      </Show>
    </AccountStreamProvider>
  );
}

/** The shell layout — extracted so AppShell stays under biome's 120-fn line limit. */
function Shell(props: {
  readonly adminHost: boolean;
  readonly nav: (path: string) => string;
  readonly navOpen: boolean;
  readonly setNavOpen: (open: boolean) => void;
  readonly railCollapsed: boolean;
  readonly onToggleRail: () => void;
  readonly bleed: boolean;
  readonly token: string | null;
  readonly onOpenPalette: () => void;
  readonly onOpenShortcuts: () => void;
  readonly children?: JSX.Element;
}): JSX.Element {
  return (
    <div
      class="app-shell"
      classList={{
        'app-shell--nav-open': props.navOpen,
        'app-shell--rail-collapsed': props.railCollapsed,
      }}
    >
      {/* First tab stop: sidebar/topbar stops sit before the content. Explicit
        focus move — solid-router intercepts same-origin anchors and
        preventDefaults the native fragment navigation, so without this the
        link scrolls (at best) but never moves keyboard focus. */}
      {/* biome-ignore lint/a11y/useValidAnchor: a skip link is navigation and
        must stay an anchor; the onClick supplements the href the router
        swallows, it doesn't replace it. */}
      <a
        class="skip-link"
        href="#main"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById('main')?.focus();
        }}
      >
        {t('nav.skip')}
      </a>

      {/* Mobile scrim behind the open nav drawer. */}
      <Show when={props.navOpen}>
        <button
          type="button"
          class="app-shell__scrim"
          aria-label={t('nav.close_menu')}
          onClick={() => props.setNavOpen(false)}
        />
      </Show>

      <AppSidebar
        adminHost={props.adminHost}
        resolve={props.nav}
        open={props.navOpen}
        onClose={() => props.setNavOpen(false)}
        collapsed={props.railCollapsed}
        onToggleRail={() => props.onToggleRail()}
      />

      <div class="app-content">
        <AppTopbar
          adminHost={props.adminHost}
          resolve={props.nav}
          navOpen={props.navOpen}
          onOpenNav={() => props.setNavOpen(true)}
          onOpenPalette={() => props.onOpenPalette()}
          onOpenShortcuts={() => props.onOpenShortcuts()}
        />

        {/* A sign-in leg that failed while this session was live lands on
            `?login_error=`; <Login/> is not mounted, so the shell says why. Both
            hosts: an admin-host sign-in fails the same way. */}
        <LoginErrorNotice />

        {/* Account-wide key-outage banner (#5119). Off the admin host: routes
            carry no org, so the banner's read would stay disabled. */}
        <Show when={!props.adminHost}>
          <KeyOutageBanner token={props.token} />
          {/* #7426: another sign-in shares this person's email — link it. */}
          <LinkCandidatesBanner token={props.token} />
        </Show>

        <main class="app-main" id="main" tabindex="-1">
          <div classList={{ page: !props.bleed }}>{props.children}</div>
        </main>

        <footer class="app-footer">
          <IconBot size="1rem" />
          <p>{t('bot.disclosure')}</p>
        </footer>
      </div>
    </div>
  );
}
